// Human wording for the risk engine (apps/api/src/lib/risk.ts) and the feature paths Claude cites
// as evidence, so reviews read as sentences instead of JSON paths.

export interface RiskFeatures {
  withdrawal: {
    id: string;
    coins: number;
    usd: number;
    method: string;
    destinationReuseOtherUsers: number;
  };
  account: {
    ageDays: number;
    emailVerified: boolean;
    status: string;
    signupCountry: string | null;
    profileCountry: string | null;
    referredBy: string | null;
    referralsMade: number;
  };
  balance: { available: number; pending: number; withdrawing: number };
  earnings: {
    conversions7d: number;
    coins7d: number;
    conversions30d: number;
    coins30d: number;
    lifetimeCoins: number;
    reversals: number;
    reversalRatio: number;
    providers: { provider: string; coins: number; n: number }[];
    conversionsWithoutClick: number;
    biggestSingleCredit: number;
    oldAccountCoins?: number;
    staffCreditCoins?: number;
    /** Streaks, app and notification bonuses, spins, prizes, referral commissions (newer reviews). */
    bonusCoins?: number;
  };
  network: {
    distinctIps30d: number;
    distinctCountries30d: string[];
    distinctAsns30d: number;
    otherUsersOnSameIps: number;
    devicesSeen: number;
    otherUsersOnSameDevice: number;
    otherUsersOnSimilarDevice: number;
    signupBotScore: number | null;
    loginCountryMatchesSignup: boolean;
  };
  history: {
    previousWithdrawals: number;
    paid: number;
    rejected: number;
    rejectedByStaff?: number;
    firstWithdrawal: boolean;
    lifetimeWithdrawnCoins: number;
  };
  /** The Android app's reports (absent on reviews from before it existed). */
  app?: {
    appInstalls: number;
    otherUsersOnSameAndroidId: number;
    otherUsersOnSameAdvertisingId: number;
    rooted: boolean;
    emulator: boolean;
    clonedApp: boolean;
    secondaryUser: boolean;
    developerMode: boolean;
    notFromPlayStore: boolean;
    androidIdHashes: string[];
    advertisingIdHashes: string[];
  };
  openFlags: { code: string; severity: string }[];
}

export const RULES: Record<string, string> = {
  email_unverified: 'Email not verified',
  account_not_active: 'Account is not active',
  account_under_1_day: 'Account less than a day old',
  payout_destination_shared: 'Payout destination used by other accounts',
  device_shared_with_many_accounts: 'Same browser as several other accounts',
  device_shared_with_another_account: 'Same browser as another account',
  many_accounts_on_identical_browser_setup: 'Many accounts on an identical browser setup',
  ip_shared_with_many_accounts: 'IP shared with several accounts',
  ip_shared_with_other_accounts: 'IP shared with other accounts',
  many_countries: 'Seen in 3+ countries in 30 days',
  country_changed_since_signup: 'Country changed since signup',
  signup_bot_score_low: 'Signup looked automated (low bot score)',
  high_reversal_ratio: 'Many offers reversed by networks',
  conversions_without_clicks: 'Credits without a recorded wall click',
  withdrawal_exceeds_lifetime_earnings:
    'Cash-out exceeds everything ever earned (offers, old account, staff credits, bonuses)',
  large_first_withdrawal: 'Large first cash-out',
  reregistered_after_deletion: 'Same email or device as a deleted account',
  // Android app: risk rules (cash-out reviews) and the flags its device report raises.
  app_device_shared_with_many_accounts: 'Same phone as several other accounts (app)',
  app_device_shared_with_another_account: 'Same phone as another account (app)',
  app_on_emulator: 'App running on an emulator',
  app_cloned: 'App running as a clone',
  app_on_rooted_phone: 'App on a rooted phone',
  app_on_secondary_user: 'App on a secondary Android user',
  app_device_shared: 'Same phone as another account (app)',
  app_device_shared_many: 'Same phone as several other accounts (app)',
  earning_country_mismatch: 'Earned from another country than the ID (VPN or proxy)',
  earning_anonymous_ip: 'Earned through a VPN, proxy or Tor (proxycheck.io)',
  vpn_or_proxy_used: 'Used a VPN, proxy or Tor (proxycheck.io)',
  app_emulator: 'App running on an emulator',
  app_rooted: 'App on a rooted phone',
  app_secondary_user: 'App on a secondary Android user',
  provider_ban: 'Banned by an offer network (its earnings taken back)',
};
/** "shared_ip" → "Shared IP": sentence case with the usual acronyms kept upper case. */
export function humanize(code: string): string {
  const t = code
    .replace(/[_.]/g, ' ')
    .replace(/\b(ip|ips|asn|id|vpn|kyc|url)\b/gi, (w) =>
      w.toLowerCase() === 'ips' ? 'IPs' : w.toUpperCase(),
    )
    .trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}
/** A risk-flag code as staff read it. */
export const flagLabel = (code: string) => RULES[code] ?? humanize(code);

export function ruleLabel(code: string): string {
  if (code.startsWith('open_flag:')) return `Open risk flag · ${flagLabel(code.slice(10))}`;
  return RULES[code] ?? humanize(code);
}

const coins = (n: number) => `${n.toLocaleString('en-US')} coins`;
const pct = (n: number) => `${Math.round(n * 100)}%`;
const yes = (b: boolean) => (b ? 'Yes' : 'No');

/** Label + formatter for each feature path. */
export const FEATURES: Record<string, { label: string; fmt?: (v: unknown) => string }> = {
  'withdrawal.coins': { label: 'Cash-out amount', fmt: (v) => coins(Number(v)) },
  'withdrawal.usd': { label: 'Cash-out value', fmt: (v) => `$${Number(v).toFixed(2)}` },
  'withdrawal.destinationReuseOtherUsers': { label: 'Other accounts with this payout destination' },
  'account.ageDays': { label: 'Account age', fmt: (v) => `${v} day${v === 1 ? '' : 's'}` },
  'account.emailVerified': { label: 'Email verified', fmt: (v) => yes(!!v) },
  'account.status': { label: 'Account status', fmt: (v) => humanize(String(v)) },
  'account.signupCountry': { label: 'Signup country', fmt: (v) => String(v ?? '—') },
  'account.profileCountry': { label: 'Profile country', fmt: (v) => String(v ?? '—') },
  'account.referredBy': { label: 'Referred by', fmt: (v) => String(v ?? 'Nobody') },
  'account.referralsMade': { label: 'Friends referred' },
  'balance.available': { label: 'Available balance', fmt: (v) => coins(Number(v)) },
  'balance.pending': { label: 'Pending balance', fmt: (v) => coins(Number(v)) },
  'balance.withdrawing': { label: 'Being withdrawn', fmt: (v) => coins(Number(v)) },
  'earnings.conversions7d': { label: 'Offers completed, 7 days' },
  'earnings.coins7d': { label: 'Earned, 7 days', fmt: (v) => coins(Number(v)) },
  'earnings.conversions30d': { label: 'Offers completed, 30 days' },
  'earnings.coins30d': { label: 'Earned, 30 days', fmt: (v) => coins(Number(v)) },
  'earnings.lifetimeCoins': { label: 'Earned, lifetime', fmt: (v) => coins(Number(v)) },
  'earnings.reversals': { label: 'Offers reversed' },
  'earnings.reversalRatio': { label: 'Reversal ratio', fmt: (v) => pct(Number(v)) },
  'earnings.providers': {
    label: 'Earnings by network',
    fmt: (v) =>
      (v as { provider: string; coins: number }[])
        .map((p) => `${p.provider} ${p.coins.toLocaleString('en-US')}`)
        .join(', ') || '—',
  },
  'earnings.conversionsWithoutClick': { label: 'Credits without a wall click' },
  'earnings.biggestSingleCredit': { label: 'Biggest single credit', fmt: (v) => coins(Number(v)) },
  'earnings.oldAccountCoins': { label: 'From the old account', fmt: (v) => coins(Number(v)) },
  'earnings.staffCreditCoins': { label: 'Staff credits', fmt: (v) => coins(Number(v)) },
  'earnings.bonusCoins': { label: 'Bonuses and referrals', fmt: (v) => coins(Number(v)) },
  'network.distinctIps30d': { label: 'IPs used, 30 days' },
  'network.distinctCountries30d': {
    label: 'Countries, 30 days',
    fmt: (v) => (v as string[]).join(', ') || '—',
  },
  'network.distinctAsns30d': { label: 'Networks (ASNs), 30 days' },
  'network.otherUsersOnSameIps': { label: 'Other accounts on the same IPs' },
  'network.devicesSeen': { label: 'Browsers seen' },
  'network.otherUsersOnSameDevice': { label: 'Other accounts on the same browser' },
  'network.otherUsersOnSimilarDevice': { label: 'Accounts on an identical browser setup' },
  'network.signupBotScore': {
    label: 'Signup bot score',
    fmt: (v) => (v === null ? 'Not measured' : `${v} / 99`),
  },
  'network.loginCountryMatchesSignup': { label: 'Same country as signup', fmt: (v) => yes(!!v) },
  'history.previousWithdrawals': { label: 'Earlier cash-outs' },
  'history.paid': { label: 'Earlier cash-outs paid' },
  'history.rejected': { label: 'Earlier cash-outs rejected' },
  'history.rejectedByStaff': { label: 'Of those, by staff' },
  'history.firstWithdrawal': { label: 'First cash-out', fmt: (v) => yes(!!v) },
  'history.lifetimeWithdrawnCoins': { label: 'Cashed out, lifetime', fmt: (v) => coins(Number(v)) },
  'app.appInstalls': { label: 'Android app installs seen' },
  'app.otherUsersOnSameAndroidId': { label: 'Other accounts with the same Android ID' },
  'app.otherUsersOnSameAdvertisingId': { label: 'Other accounts with the same advertising ID' },
  'app.rooted': { label: 'Rooted phone (app says)', fmt: (v) => yes(!!v) },
  'app.emulator': { label: 'Emulator (app says)', fmt: (v) => yes(!!v) },
  'app.clonedApp': { label: 'Cloned app (app says)', fmt: (v) => yes(!!v) },
  'app.secondaryUser': { label: 'Secondary Android user (app says)', fmt: (v) => yes(!!v) },
  'app.developerMode': { label: 'Developer mode on (app says)', fmt: (v) => yes(!!v) },
  'app.notFromPlayStore': { label: 'Installed outside Google Play', fmt: (v) => yes(!!v) },
  'app.androidIdHashes': {
    label: 'Android ID (hash prefixes)',
    fmt: (v) => (v as string[]).join(', ') || '—',
  },
  'app.advertisingIdHashes': {
    label: 'Advertising ID (hash prefixes)',
    fmt: (v) => (v as string[]).join(', ') || '—',
  },
  openFlags: {
    label: 'Open risk flags',
    fmt: (v) => (v as { code: string }[]).map((f) => flagLabel(f.code)).join(', ') || 'None',
  },
};

export function featureAt(features: unknown, path: string): unknown {
  let cur: unknown = features;
  for (const k of path.replace(/\[(\d+)\]/g, '.$1').split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[k];
  }
  return cur;
}

/** "earnings.reversalRatio" → { label: 'Reversal ratio', value: '12%' } (unknown paths pass through). */
export function describeRef(
  features: unknown,
  ref: string,
): { label: string; value: string | null } {
  const path = ref.replace(/^features\./, '');
  const meta = FEATURES[path];
  const v = featureAt(features, path);
  const value =
    v === undefined
      ? null
      : meta?.fmt
        ? meta.fmt(v)
        : typeof v === 'object'
          ? JSON.stringify(v)
          : String(v);
  return { label: meta?.label ?? path, value };
}
